import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaTimesCircle } from 'react-icons/fa';
import GradientButton from '../../components/GradientButton';


const PaymentCanceled = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  
  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  // Usuń Payment z bazy gdy użytkownik anuluje płatność
  useEffect(() => {
    const deletePayment = async () => {
      try {
        const orderId = searchParams.get('ext_order_id') || searchParams.get('orderId');
        
        if (orderId && userInfo && userInfo.token) {
          const deleteUrl = API_BASE ? `${API_BASE}/api/payment/delete/${orderId}/` : `/api/payment/delete/${orderId}/`;
          await fetch(deleteUrl, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${userInfo.token}`
            }
          });
          console.log(`Payment ${orderId} deleted after cancellation`);
        }
      } catch (err) {
        console.error('Błąd usuwania płatności:', err);
      }
    };
    
    deletePayment();
  }, [searchParams, API_BASE, userInfo]);

  const handleBackToPricing = () => {
    navigate('/pricing');
  };

  const handleRetryPayment = () => {
    navigate('/pricing');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(102, 126, 234, 0.1)',
        padding: '2rem'
      }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8} xl={6}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Card
                className="border-0 shadow-lg"
                style={{
                  borderRadius: '30px',
                  overflow: 'hidden'
                }}
              >
                {/* Canceled Icon */}
                <div
                  className="text-center py-5"
                  style={{
                    background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
                  }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  >
                    <div
                      style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto'
                      }}
                    >
                      <FaTimesCircle size={70} style={{ color: '#ff6b6b' }} />
                    </div>
                  </motion.div>
                </div>

                <Card.Body className="p-5 text-center">
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <h1 className="display-4 fw-bold mb-3" style={{ color: '#ff6b6b' }}>
                      Płatność anulowana
                    </h1>
                    <h3 className="mb-4 text-muted">
                      Wróć do wyboru planu
                    </h3>

                    <p className="lead mb-4" style={{ fontSize: '1.2rem', lineHeight: '1.8' }}>
                      Zdecydowałeś się anulować płatność. Możesz spróbować ponownie 
                      w każdej chwili, gdy będziesz gotowy.
                    </p>

                    {/* Why Subscribe Section */}
                    <Row className="g-3 mb-4 mt-5">
                      <Col xs={12} md={4}>
                        <Card
                          className="border-0 h-100"
                          style={{
                            background: 'rgba(118, 75, 162, 0.1)',
                            borderRadius: '15px'
                          }}
                        >
                          <Card.Body className="p-3">
                            <div style={{ fontSize: '2rem' }} className="mb-2">
                              📊
                            </div>
                            <h6 className="fw-bold mb-1">Pełna kontrola</h6>
                            <small className="text-muted">
                              Zarządzaj urlopami i zatrudnieniem
                            </small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xs={12} md={4}>
                        <Card
                          className="border-0 h-100"
                          style={{
                            background: 'rgba(118, 75, 162, 0.1)',
                            borderRadius: '15px'
                          }}
                        >
                          <Card.Body className="p-3">
                            <div style={{ fontSize: '2rem' }} className="mb-2">
                              📝
                            </div>
                            <h6 className="fw-bold mb-1">Generator CV</h6>
                            <small className="text-muted">
                              Profesjonalne CV w kilka minut
                            </small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xs={12} md={4}>
                        <Card
                          className="border-0 h-100"
                          style={{
                            background: 'rgba(118, 75, 162, 0.1)',
                            borderRadius: '15px'
                          }}
                        >
                          <Card.Body className="p-3">
                            <div style={{ fontSize: '2rem' }} className="mb-2">
                              💰
                            </div>
                            <h6 className="fw-bold mb-1">Kalkulatory</h6>
                            <small className="text-muted">
                              Wynagrodzenia i świadczenia
                            </small>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    {/* Action Buttons */}
                    <div className="mt-5 d-flex gap-3 flex-column flex-md-row justify-content-center">
                      <GradientButton
                        onClick={handleRetryPayment}
                        gradient='purple'
                      >
                        Spróbuj ponownie
                      </GradientButton>
                      <Button
                        onClick={handleBackToPricing}
                        variant="outline-secondary"
                        style={{
                          padding: '12px 30px',
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          borderRadius: '15px'
                        }}
                      >
                        Powrót do planów
                      </Button>
                    </div>


                  </motion.div>
                </Card.Body>

                {/* Support Info */}
                <div className="text-center pb-4">
                  <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                    Masz pytania? 
                  </p>
                  <a href="mailto:support@sigmawork.pl" style={{ color: 'var(--bs-primary)', textDecoration: 'none', fontWeight: 'bold' }}>
                    Skontaktuj się z nami
                  </a>
                </div>
              </Card>
            </motion.div>

            {/* Additional Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-center mt-4"
            >
              <Card
                className="border-0"
                style={{
                  borderRadius: '20px',
                  padding: '1.5rem'
                }}
              >
                <p className="mb-2 text-muted" style={{ fontSize: '0.95rem' }}>
                  Płatności są szyfrowane i bezpieczne
                </p>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PaymentCanceled;
